import { Box, TableSortLabel, Tooltip, FormControl } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import { visuallyHidden } from '@mui/utils';
import React, { FC, useEffect, useState, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TableVirtuoso } from 'react-virtuoso';
import updateHotAlertState from '../../../api/hotAlert/updateHotAlertState';
import { TenantContext } from '../../../contexts/tenantContext';
import { colors, fontSize, fontWeight, fontFamilyPrimary } from '../../../theme';
import { ArrowDropDownIconStyled } from '../../../theme/style';
import { OperatorsEnum } from '../../../utils/enum/OperatorsType';
import { QUESTION_TYPE } from '../../Modals/DashboardModal/constants';
import { SurveyTemporalFilterType } from '../../Surveys/surveyInterface';
import { ColumnLabels, CreateRowCellInterface, hotAlertsColors } from '../constants';
import { GetHotAlertsInterface, HotAlertStatusInterface, ColorHotAlertInterface, SATISFACTION_LEVEL, HotAlertColumnInterface, HotAlertSatisfactionInterface, HotAlertsCountInterface, FilterDateInterface } from '../hotAlertsInterface';
import { TableCellStyled, SelectStatusHotAlertStyled, MenuItemStyled } from '../styled';
import HotAlertDateFilter from './HotAlertDateFilter';
import HotAlertFilterDropdown from './HotAlertFilterDropdown';
import { TableCellBodyStyled, TableCellHeaderStyled, TableRowBodyStyled, TableRowHeaderStyled, TextFieldDivStyled, TextFieldStyled, TableContainerStyled } from './styled';
import './styles.css';
import { ReactComponent as EmojiAngry } from '../../../assets/hotAlert/angry.svg';
import { ReactComponent as EmojiHappy } from '../../../assets/hotAlert/happy.svg';
import { ReactComponent as EmojiNeutral } from '../../../assets/hotAlert/neutral.svg';

type HotAlertTableProps = {
    hotalerts: GetHotAlertsInterface
    surveyId: string
    handleHotAlertCounts: (hotAlertCounts: HotAlertsCountInterface) => void
    handleNumberOfFilteredHotAlerts: (numberOfFilteredHotAlert: number) => void
    temporalFilterType: SurveyTemporalFilterType
}

function EnhancedTableHead(props: any) {
    const { headCells, order, orderBy, onRequestSort, onRequestFilter, lastFocusedItem, onRequestFilterDate } = props;
    const { t } = useTranslation(['dashboard', 'hotAlerts']);

    const createSortHandler = (property: any, type: QUESTION_TYPE) => (event: any) => {
        onRequestSort(event, property, type);
    };

    const createFilterHandler = (property: any, filterText: string[]) => {
        onRequestFilter(property, filterText);
    };

    const createFilterDateHandler = (property: any, filterDate: FilterDateInterface) => {
        onRequestFilterDate(property, filterDate);
    };

    return (
        <TableRowHeaderStyled>
            {headCells.map((headCell: any) => (
                <TableCellHeaderStyled
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    style={{
                        minWidth: '200px',
                        maxWidth: '500px',
                        width: 'auto',
                        position: 'sticky',
                        right: headCell.id === 'status' ? 0 : 'auto',
                        backgroundColor: colors.grey800,
                        zIndex: headCell.id === 'status' ? 10 : 1,
                        borderLeftStyle: headCell.id === 'status' ? 'double' : 'none',
                        borderWidth: headCell.id === 'status' ? '3px' : 'none',
                        borderColor: headCell.id === 'status' ? colors.grey600 : 'none',
                    }}
                >
                    <TableCellStyled paddingcell='5px'>
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id, headCell.type)}
                            title={headCell.label}
                        >
                            {headCell.shortlabel}
                            {orderBy === headCell.id ? (
                                <Box component='span' sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCellStyled>
                    <TextFieldDivStyled>
                        {headCell.type === QUESTION_TYPE.CLOSED_DATA && (
                            <HotAlertFilterDropdown
                                columnId={headCell.id}
                                filterValues={headCell.modalities}
                                handleSelectedFilterValues={createFilterHandler}
                                selectedFilterValues={headCell.filter}
                            />
                        )}
                        {headCell.type === QUESTION_TYPE.OPEN && (
                            <TextFieldStyled
                                autoFocus={headCell.id === lastFocusedItem}
                                id={`filter-${headCell.id}`}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    createFilterHandler(headCell.id, [event.target.value]);
                                }}
                                placeholder={t('dashboard:addIndicatorModal.selectDisplayType.indicatorChart.filterContent')}
                                size="small"
                                type="search"
                                value={headCell.filter || ''}
                                variant="standard"
                                hiddenLabel
                            />
                        )}
                        {headCell.type === QUESTION_TYPE.DATE && (
                            <HotAlertDateFilter
                                columnId={headCell.id}
                                handleSelectedFilterDateValue={createFilterDateHandler}
                                selectedFilterDateValue={headCell.filterDate}
                            />
                        )}
                    </TextFieldDivStyled>
                </TableCellHeaderStyled>
            ))}
        </TableRowHeaderStyled>
    );
}

function compare(a: any, b: any, property: any) {
    const aEntryValue = property === 'status' ? a[property].label : a[property];
    const bEntryValue = property === 'status' ? b[property].label : b[property];
    const aValue = aEntryValue ? String(aEntryValue).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';
    const bValue = bEntryValue ? String(bEntryValue).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';
    if (aValue < bValue) {
        return -1;
    }
    if (aValue > bValue) {
        return 1;
    }

    return 0;
}

function getAnswerValue(answers: any[], questionCode: number) {
    const answer = answers.find((ans: any) => ans.questionid === questionCode);

    if (!answer) {
        return '';
    }

    return Array.isArray(answer.answer) ? answer.answer.join(', ') : answer.answer;
}

function getSatisfactionLevel(answers: any[], satColumn: HotAlertColumnInterface, satisfactionConfiguration?: HotAlertSatisfactionInterface) {
    const answer = answers.find((ans: any) => ans.questionid === satColumn.code);
    if (!answer || !satisfactionConfiguration) {
        return '';
    }
    const isSatisfy = satisfactionConfiguration.positiveModalityIds.some((value) => answer.modality_code.includes(value));
    const isUnsatisfy = (satisfactionConfiguration.negativeModalityIds ?? []).some((value) => answer.modality_code.includes(value));

    if (isSatisfy) {
        return SATISFACTION_LEVEL.SATISFY;
    }

    return isUnsatisfy ? SATISFACTION_LEVEL.UNSATISFY : SATISFACTION_LEVEL.NEUTRAL;
}

const VirtualizedHotAlertTable: FC<HotAlertTableProps> = ({ hotalerts, surveyId, handleHotAlertCounts, handleNumberOfFilteredHotAlerts, temporalFilterType }) => {
    const [headCells, setHeadCells] = useState<Array<any>>([]);
    const [data, setData] = useState<Array<any>>([]);
    const [filteredData, setFilteredData] = useState<Array<any>>([]);
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState('');
    const [filters, setFilters] = useState<{[key: string]: string[]}>({});
    const [filterDate, setFilterDate] = useState<{[key: string]: FilterDateInterface}>({});
    const [lastFocusedItem, setLastFocusedItem] = useState('');
    const { t } = useTranslation(['dashboard', 'hotAlerts']);
    const navigate = useNavigate();
    const [selectedSchema] = useContext(TenantContext) ?? [];

    useEffect(() => {
        // Créer les colonnes (status en dernier pour qu'elle soit sticky à droite)
        const dynamicCells = hotalerts.columns.map((column: any, index) => {
            let rank = index;
            if (column.isopen) {
                rank = -1;
            }
            if (column.issatisfaction) {
                rank = -2;
            }

            return {
                id: `col_${column.id}`,
                code: column.code,
                shortlabel: column.shortlabel.length > 30 ? `${column.shortlabel.substring(0, 30)}...` : column.shortlabel,
                label: column.label,
                type: column.type,
                filter: [],
                filterDate: { date: null, operator: null },
                isOpen: column.isopen,
                isSatisfaction: column.issatisfaction,
                rank,
                modalities: column.modalities,
            };
        });

        if (temporalFilterType === SurveyTemporalFilterType.DATE) {
            // Ajouter la colonne date
            dynamicCells.push({
                id: 'date',
                code: -9998,
                label: t('hotAlerts:summary.date'),
                shortlabel: t('hotAlerts:summary.date'),
                type: QUESTION_TYPE.DATE,
                filter: [],
                filterDate: { date: null, operator: null },
                isOpen: false,
                isSatisfaction: false,
                rank: -3,
                modalities: null,
            });
        }

        // Ajouter la colonne status en dernier (pour sticky right), uniquement si des status sont configurés
        if (hotalerts.status?.length > 0) {
            dynamicCells.push({
                id: 'status',
                code: -9999,
                label: t('hotAlerts:summary.status'),
                shortlabel: t('hotAlerts:summary.status'),
                type: QUESTION_TYPE.CLOSED_DATA,
                filter: [],
                filterDate: { date: null, operator: null },
                isOpen: false,
                isSatisfaction: false,
                rank: 99999,
                modalities: hotalerts.status.map((st: HotAlertStatusInterface) => st.label),
            });
        }

        dynamicCells.sort((a, b) => a.rank - b.rank);
        setHeadCells(dynamicCells);

        const getStatusById: (statusId?: string) => HotAlertStatusInterface | undefined = (statusId?: string) => {
            if (statusId) {
                return hotalerts.status.find((st: HotAlertStatusInterface) => String(st.id) === statusId);
            }

            return hotalerts.status.find((st: HotAlertStatusInterface) => st.isDefault);
        };

        // Créer les données
        const rowsData = hotalerts.datas.map((dataItem: any) => {
            const satisfactionColumn = hotalerts.columns.find((col) => (col.issatisfaction));
            const rowData: any = {
                id: dataItem.interviewid,
                hotAlertId: dataItem.hotAlertId,
                date: dataItem.interviewDate,
                status: getStatusById(dataItem.status),
            };

            hotalerts.columns.forEach((column: any) => {
                const columnKey = `col_${column.id}`;
                rowData[columnKey] = getAnswerValue(dataItem.answers, column.code);
                if (satisfactionColumn && (column.id === satisfactionColumn.id)) {
                    rowData.satisfaction = getSatisfactionLevel(dataItem.answers, satisfactionColumn, hotalerts.satisfactionConfiguration);
                }
            });

            return rowData;
        });
        setData(rowsData);
        setFilteredData(rowsData);
    }, [hotalerts]);

    useEffect(() => {
        const newHotAlertCounts: HotAlertsCountInterface = {
            numberToTreat: data.filter((d) => d.status?.isDefault).length,
            numberOfClosed: data.filter((d) => d.status?.isFinal).length,
        };
        handleHotAlertCounts(newHotAlertCounts);
    }, [data]);

    useEffect(() => {
        handleNumberOfFilteredHotAlerts(filteredData.length);
    }, [filteredData]);

    const getStatusByLabel: (statusLabel?: string) => HotAlertStatusInterface | undefined = (statusLabel?: string) => {
        if (statusLabel) {
            return hotalerts.status.find((st: HotAlertStatusInterface) => String(st.label) === statusLabel);
        }

        return hotalerts.status.find((st: HotAlertStatusInterface) => st.isDefault);
    };

    const handleRequestSort = (event: any, property: any, type: QUESTION_TYPE) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        const sortedData = [...filteredData].sort((a, b) => {
            if (type === QUESTION_TYPE.DATE) {
                // Handle date sorting
                const dateA = new Date(a[property]);
                const dateB = new Date(b[property]);

                if (newOrder === 'asc') {
                    return dateA.getTime() - dateB.getTime();
                }

                return dateB.getTime() - dateA.getTime();
            }

            return newOrder === 'asc' ? compare(a, b, property) : -compare(a, b, property);
        });

        setFilteredData(sortedData);
        setOrder(newOrder);
        setOrderBy(property);
    };

    const applyDateFilter = (row: any, key: string, filterValues: FilterDateInterface): boolean => {
        if (!filterValues.date || !filterValues.operator) {
            return true; // No filter applied, include the row
        }

        const rowDate = new Date(row[key]);
        const tmpFilterDate = filterValues.date;
        const rowDateOnly = new Date(rowDate.getFullYear(), rowDate.getMonth(), rowDate.getDate());
        const tmpFilterDateOnly = new Date(tmpFilterDate.getFullYear(), tmpFilterDate.getMonth(), tmpFilterDate.getDate());

        switch (filterValues.operator) {
            case OperatorsEnum.EQUAL:
                return rowDate.toDateString() === tmpFilterDate.toDateString();

            case OperatorsEnum.SUPERIOR:
                return rowDateOnly > tmpFilterDateOnly;

            case OperatorsEnum.SUPERIOR_EQUAL:
                return rowDateOnly >= tmpFilterDateOnly;

            case OperatorsEnum.INFERIOR:
                return rowDateOnly < tmpFilterDateOnly;

            case OperatorsEnum.INFERIOR_EQUAL:
                return rowDateOnly <= tmpFilterDateOnly;

            default:
                return true; // Unknown operator, include the row
        }
    };

    // Extract the general filtering logic
    const applyGeneralFilters = (dataset: any[], applyFilters: any, applyHeadCells: any[]) => {
        let filtered = [...dataset];

        Object.keys(applyFilters).forEach((key) => {
            const filterValues = applyFilters[key];
            const currentHeadCell = applyHeadCells.find((hc) => hc.id === key);

            if (currentHeadCell && filterValues.length > 0) {
                if (currentHeadCell.type === QUESTION_TYPE.CLOSED_DATA) {
                    if (currentHeadCell.id === 'status') {
                        filtered = filtered.filter((row) => (
                            filterValues.findIndex((val: any) => val === row[key].label) >= 0
                        ));
                    } else {
                        filtered = filtered.filter((row) => (
                            filterValues.findIndex((val: any) => val === row[key]) >= 0
                        ));
                    }
                }
                if (currentHeadCell.type === QUESTION_TYPE.OPEN) {
                    filtered = filtered.filter((row) => String(row[key] || '').toLowerCase().includes(filterValues[0].toLowerCase()));
                }
            }
        });

        return filtered;
    };

    // Extract the date filtering logic
    const applyDateFilters = (dataset: any[], applyFilterDate: any, applyHeadCells: any[]) => {
        let filtered = [...dataset];

        Object.keys(applyFilterDate).forEach((key) => {
            const filterValues = applyFilterDate[key];
            const currentHeadCell = applyHeadCells.find((hc) => hc.id === key);

            if (currentHeadCell && filterValues) {
                filtered = filtered.filter((row) => applyDateFilter(row, key, filterValues));
            }
        });

        return filtered;
    };

    const handleFilter = (property: string, filterText: string[]) => {
        const newFilters = { ...filters };
        newFilters[property] = filterText;
        setLastFocusedItem(property);
        setFilters(newFilters);
        setHeadCells((prev) => {
            const headCellsCopy = [...prev];
            const indexOf = headCellsCopy.findIndex((hc) => hc.id === property);

            if (indexOf !== -1) {
                headCellsCopy[indexOf].filter = filterText;
            }

            return headCellsCopy;
        });

        // Apply general filters first
        let filtered = applyGeneralFilters(data, newFilters, headCells);
        // Then apply date filters
        filtered = applyDateFilters(filtered, filterDate, headCells);
        setFilteredData(filtered);
    };

    const handleFilterDate = (property: string, entryFilterDate: FilterDateInterface) => {
        const newFilterDate = { ...filterDate };
        newFilterDate[property] = entryFilterDate;
        setLastFocusedItem(property);
        setFilterDate(newFilterDate);
        setHeadCells((prev) => {
            const headCellsCopy = [...prev];
            const indexOf = headCellsCopy.findIndex((hc) => hc.id === property);

            if (indexOf !== -1) {
                headCellsCopy[indexOf].filterDate = entryFilterDate;
            }

            return headCellsCopy;
        });

        // Apply general filters first
        let filtered = applyGeneralFilters(data, filters, headCells);
        // Then apply date filters
        filtered = applyDateFilters(filtered, newFilterDate, headCells);

        setFilteredData(filtered);
    };

    const scrollbarStyles = {
        '&::-webkit-scrollbar': {
            height: '8px',
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            background: colors.grey600,
            borderRadius: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: colors.grey400,
            borderRadius: '6px',
            '&:hover': {
                background: '#555',
            },
        },
    };

    const updateStatus = (newStatus: string, respondantId: number) => {
        const newData = [...data];
        const indexOf = newData.findIndex((nd) => nd.id === respondantId);
        const newStatusToApply = getStatusByLabel(newStatus);
        if (newStatusToApply) {
            newData[indexOf].status = newStatusToApply;
            setData(newData);
            updateHotAlertState(surveyId, String(respondantId), String(newStatusToApply.id), t('hotAlerts:actions.statusUpdated'), t('hotAlerts:actions.statusNotUpdated'), selectedSchema || '');
        }
    };

    const virtuosoComponents = useMemo(() => {
        const Scroller = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof TableContainer>>((props, ref) => (
            <TableContainer
                {...props}
                ref={ref}
                style={{
                    // eslint-disable-next-line
                    ...props.style,
                    overflow: 'auto',
                }}
                sx={{ ...scrollbarStyles }}
            />
        ));
        Scroller.displayName = 'VirtuosoScroller';

        const VirtuosoTable = (props: React.ComponentProps<typeof Table>) => (
            <Table
                stickyHeader
                {...props}
                style={{
                    borderCollapse: 'separate',
                    tableLayout: 'auto', // Add for better performance
                    width: '100%',
                }}
            />
        );

        const VirtuosoTableHead = React.forwardRef<HTMLTableSectionElement, any>((props, ref) => (
            <TableHead {...props} ref={ref} />
        ));
        VirtuosoTableHead.displayName = 'VirtuosoTableHead';

        const VirtuosoTableBody = React.forwardRef<HTMLTableSectionElement, any>((props, ref) => (
            <TableBody {...props} ref={ref} />
        ));
        VirtuosoTableBody.displayName = 'VirtuosoTableBody';

        // TableRow that uses data-index for zebra background
        const VirtuosoTableRow = (props: any) => (
            <TableRowBodyStyled
                // eslint-disable-next-line
                bgcolor={props['data-index'] % 2 === 0 ? '#ffffff' : colors.grey800}
                {...props}
                hover
            />
        );

        return {
            Scroller,
            Table: VirtuosoTable,
            TableHead: VirtuosoTableHead,
            TableRow: VirtuosoTableRow,
            TableBody: VirtuosoTableBody,
        };
        // Depend only on stable values; scrollbarStyles is stable (local const) and colors/imports are stable
    }, []);

    const CreateRowCell = ({
        currentColumn, row, status, statusList, respondantId, handleChangeStatus, satisfaction,
    }: CreateRowCellInterface) => {
        function colorStatus(label: string, entryStatusList: Array<HotAlertStatusInterface>): ColorHotAlertInterface {
            const statusColor = entryStatusList.find((item) => item.label === label);

            return statusColor ? statusColor.color : hotAlertsColors.GREY;
        }

        if (!row) {
            return null; // Changed from 'return;' to 'return null;'
        }
        if (currentColumn.isOpen && row) {
            return (
                <Tooltip
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: colors.grey100,
                                borderRadius: '4px',
                                fontFamily: fontFamilyPrimary,
                                fontSize: fontSize.s,
                                padding: '4px 8px',
                                color: colors.grey000,
                                fontWeight: fontWeight.semiRegular,
                                textAlign: 'center',
                                '& .MuiTooltip-arrow': {
                                    color: colors.grey100,
                                },
                            },
                        },
                    }}
                    placement='bottom-start'
                    title={row}
                    arrow
                >
                    <span>{row}</span>
                </Tooltip>
            );
        }

        if (String(currentColumn.id) === ColumnLabels.STATUS) {
            return (
                <FormControl>
                    <SelectStatusHotAlertStyled
                        IconComponent={ArrowDropDownIconStyled}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    marginTop: 0.5,
                                    '& .MuiList-root.MuiList-padding.MuiMenu-list': {
                                        padding: 0,
                                    },
                                },
                            },
                        }}
                        background={colorStatus(status.label, statusList).color}
                        fontcolor={colorStatus(status.label, statusList).fontColor}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            handleChangeStatus(event.target.value, respondantId);
                        }}
                        value={status.label}
                        displayEmpty
                    >
                        {statusList.map(({ label }) => (
                            <MenuItemStyled key={label} value={label}>
                                {label}
                            </MenuItemStyled>
                        ))}
                    </SelectStatusHotAlertStyled>
                </FormControl>
            );
        }

        if (currentColumn.isSatisfaction && row && satisfaction) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    {satisfaction === SATISFACTION_LEVEL.SATISFY && (<EmojiHappy />)}
                    {satisfaction === SATISFACTION_LEVEL.UNSATISFY && (<EmojiAngry />)}
                    {satisfaction === SATISFACTION_LEVEL.NEUTRAL && (<EmojiNeutral />)}
                    <span title={row}>
                        {row}
                    </span>
                </div>
            );
        }

        if (currentColumn.type === QUESTION_TYPE.DATE && row) {
            const dateToDisplay = new Date(row).toLocaleString();

            return (
                <span title={dateToDisplay}>
                    {dateToDisplay}
                </span>
            );
        }

        if (row) {
            return (
                <span title={row}>
                    {row}
                </span>
            );
        }

        // Add explicit default return
        return null;
    };

    return (
        <div style={{ width: '100%', height: '70%', display: 'flex', flexDirection: 'column' }}>
            <TableContainerStyled>
                <TableVirtuoso
                    components={virtuosoComponents}
                    data={filteredData}
                    fixedHeaderContent={() => (
                        <EnhancedTableHead
                            headCells={headCells}
                            lastFocusedItem={lastFocusedItem}
                            onRequestFilter={handleFilter}
                            onRequestFilterDate={handleFilterDate}
                            onRequestSort={handleRequestSort}
                            order={order}
                            orderBy={orderBy}
                        />
                    )}
                    increaseViewportBy={200}
                    itemContent={(index, row) => (
                        <>
                            {headCells.map((headCell) => (
                                <TableCellBodyStyled
                                    key={headCell.id}
                                    // eslint-disable-next-line
                                    bgcolor={headCell.id === 'status' ? (index % 2 === 0 ? '#ffffff' : colors.grey800) : 'transparent'}
                                    // eslint-disable-next-line
                                    onClick={() => headCell.id !== 'status' && navigate(`${row.id}/`)}
                                    style={{
                                        maxWidth: '500px',
                                        width: 'auto',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        position: headCell.id === 'status' ? 'sticky' : 'relative',
                                        right: headCell.id === 'status' ? 0 : 'auto',
                                        zIndex: 1,
                                        borderBottom: `1px solid ${colors.grey600}`,
                                        borderLeftStyle: headCell.id === 'status' ? 'double' : 'none',
                                        borderLeftWidth: headCell.id === 'status' ? '3px' : 'none',
                                        borderColor: headCell.id === 'status' ? colors.grey600 : 'none',
                                    }}
                                >
                                    <CreateRowCell
                                        currentColumn={headCell}
                                        handleChangeStatus={updateStatus}
                                        // eslint-disable-next-line
                                        respondantId={row.id}
                                        row={row[headCell.id] || ''}
                                        // eslint-disable-next-line
                                        satisfaction={row.satisfaction}
                                        // eslint-disable-next-line
                                        status={row.status}
                                        statusList={hotalerts.status}
                                    />
                                </TableCellBodyStyled>
                            ))}
                        </>
                    )}
                    overscan={5}
                    style={{
                        height: '100%',
                        width: '100%',
                    }}
                />
            </TableContainerStyled>
        </div>
    );
};

export default VirtualizedHotAlertTable;
